import type { ComponentType } from 'react'
import { Navigate, Outlet, type RouteObject } from 'react-router'

import { RoleGuard } from 'features/settings/components/RoleGuard'
import {
  getAllowlistFor,
  SETTINGS_PAGE_KEYS,
  CONNECTOR_LIBRARY_NAV_ITEM,
  EXPORT_GROUPS_NAV_ITEM,
  SOURCE_INTEGRATIONS_NAV_ITEM,
} from 'features/settings/nav-config'
import {
  ACCESS_URL,
  AGENTS_URL,
  CONNECTORS_URL,
  INTEGRITY_REPORT_URL,
  CUSTOMER_BRANDS_URL,
  CONNECTOR_LIBRARY_URL,
  DIGEST_URL,
  EXPORT_GROUPS_URL,
  EXPORT_URL,
  HUB_ACCOUNTS_URL,
  HUB_SETTINGS_URL,
  PROFILE_URL,
  RUN_SCHEDULED_AGENTS_URL,
  SETTINGS_URL,
  SYSTEM_PROMPT_URL,
  SOURCE_INTEGRATIONS_URL,
  INTEGRITY_FIX_URL,
} from 'features/settings/paths'
import { lazyComponent, lazyDefault } from 'routes/lazy-route'
import { ClientAuthPolicy } from 'src/types/auth-role'

// One guarded tab: a static RoleGuard parent (tiny, stays eager so the gate
// runs before any page code downloads) wrapping a lazy-loaded leaf. `subpages`
// sit under the same guard, so a page reached from the tab can never be more
// visible than the tab itself.
const guardedTab = (
  path: string,
  pageKey: (typeof SETTINGS_PAGE_KEYS)[keyof typeof SETTINGS_PAGE_KEYS],
  load: () => Promise<Record<string, ComponentType>>,
  exportName: string,
  subpages: RouteObject[] = [],
): RouteObject => ({
  path,
  element: (
    <RoleGuard pageKey={pageKey} allowlist={getAllowlistFor(path)!}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    { index: true, lazy: lazyComponent(load, exportName) },
    ...subpages,
  ],
})

export const settingsRoutes: RouteObject[] = [
  {
    path: SETTINGS_URL,
    element: (
      <RoleGuard
        pageKey={SETTINGS_PAGE_KEYS.SETTINGS}
        allowlist={ClientAuthPolicy.ONLY_AUTHENTICATED_USERS}
      >
        <Outlet />
      </RoleGuard>
    ),
    handle: { featureKey: SETTINGS_URL },
    children: [
      {
        // SettingsShell renders the tab nav + its own <Outlet/> for the tabs
        // below. Pathless — the path is already matched by the parent.
        lazy: lazyDefault(() => import('features/settings/Container')),
        children: [
          { index: true, element: <Navigate to={PROFILE_URL} replace /> },
          guardedTab(
            ACCESS_URL,
            SETTINGS_PAGE_KEYS.ACCESS,
            () => import('features/settings/features/access'),
            'AccessContainer',
          ),
          guardedTab(
            HUB_SETTINGS_URL,
            SETTINGS_PAGE_KEYS.DATASETS,
            () => import('features/settings/features/hub-settings'),
            'HubSettingsContainer',
          ),
          guardedTab(
            HUB_ACCOUNTS_URL,
            SETTINGS_PAGE_KEYS.DATASET_ACCOUNTS,
            () => import('features/settings/features/dataset-accounts'),
            'HubAccountsContainer',
          ),
          guardedTab(
            CUSTOMER_BRANDS_URL,
            SETTINGS_PAGE_KEYS.CUSTOMER_BRANDS,
            () => import('features/settings/features/customer-brands'),
            'CustomerBrandsContainer',
          ),
          guardedTab(
            SYSTEM_PROMPT_URL,
            SETTINGS_PAGE_KEYS.SYSTEM_PROMPT,
            () => import('features/settings/features/system-prompt'),
            'SystemPromptContainer',
          ),
          guardedTab(
            RUN_SCHEDULED_AGENTS_URL,
            SETTINGS_PAGE_KEYS.RUN_SCHEDULED_AGENTS,
            () => import('features/settings/features/run-scheduled-agents'),
            'RunScheduledAgentsContainer',
          ),
          guardedTab(
            AGENTS_URL,
            SETTINGS_PAGE_KEYS.AGENTS,
            () => import('features/settings/features/agents'),
            'AgentsContainer',
          ),
          guardedTab(
            DIGEST_URL,
            SETTINGS_PAGE_KEYS.DIGEST,
            () => import('features/settings/features/digest'),
            'DigestContainer',
          ),
          {
            // Loading-aware flag guard (reads useFeatureFlags() tri-state, not
            // the bare boolean). The detail container branches admin vs member
            // by real role — there is no per-role route. The guard and the
            // index page share one module, so lazying the guard costs nothing
            // extra: the module is already the index page's chunk.
            path: CONNECTORS_URL,
            lazy: lazyComponent(
              () => import('features/settings/features/connectors/Container'),
              'ConnectorsFlagGuard',
            ),
            children: [
              {
                index: true,
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/connectors/Container'),
                  'ConnectorsIndexContainer',
                ),
              },
              {
                path: ':slug',
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/connectors/features/connector-detail/Container'),
                  'ConnectorDetailContainer',
                ),
              },
            ],
          },
          {
            path: SOURCE_INTEGRATIONS_URL,
            element: (
              <RoleGuard
                pageKey={SETTINGS_PAGE_KEYS.SOURCE_INTEGRATIONS}
                allowlist={SOURCE_INTEGRATIONS_NAV_ITEM.roleAllowlist}
              >
                <Outlet />
              </RoleGuard>
            ),
            children: [
              {
                index: true,
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/source-integrations/Container'),
                  'SourceIntegrationsContainer',
                ),
              },
              {
                path: 'new',
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/source-integrations/features/source-editor'),
                  'SourceEditorContainer',
                ),
              },
              {
                path: ':sourceUuid',
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/source-integrations/features/source-editor'),
                  'SourceEditorContainer',
                ),
              },
            ],
          },
          {
            // Allowlist read from the nav config rather than restated, so the
            // route and the sidebar item cannot drift into disagreeing about
            // who may open this page. It resolves because the item is part of
            // the static SETTINGS_NAV_ITEMS array.
            //
            // Permissive on purpose, mirroring the Ask Yogi connectors route
            // above: reading the library is a MEMBER scope server-side
            // (source_connection:read), and only the writes are admin-only.
            // Operator-only launch posture: the API authorizes account-level
            // (the Source Integrations pattern), so the coarse role filter here
            // mirrors the nav item's; the account scope is the real gate.
            path: CONNECTOR_LIBRARY_URL,
            element: (
              <RoleGuard
                pageKey={SETTINGS_PAGE_KEYS.DATA_CONNECTIONS}
                allowlist={CONNECTOR_LIBRARY_NAV_ITEM.roleAllowlist}
              >
                <Outlet />
              </RoleGuard>
            ),
            children: [
              {
                index: true,
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/connector-library/Container'),
                  'ConnectorLibraryContainer',
                ),
              },
              {
                // Entering a credential is its own page, so the form routes
                // nest UNDER the connector rather than beside it: that is what
                // makes the form's `navigate('..')` land back on this
                // connector's detail.
                path: ':connectorType',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    lazy: lazyComponent(
                      () =>
                        import('features/settings/features/connector-library/features/connector-detail/Container'),
                      'ConnectorDetailContainer',
                    ),
                  },
                  {
                    path: 'new',
                    lazy: lazyComponent(
                      () =>
                        import('features/settings/features/connector-library/features/credential-form/Container'),
                      'CredentialFormContainer',
                    ),
                  },
                  {
                    path: 'connections/:connectionUuid/replace',
                    lazy: lazyComponent(
                      () =>
                        import('features/settings/features/connector-library/features/credential-form/Container'),
                      'CredentialFormContainer',
                    ),
                  },
                ],
              },
            ],
          },
          guardedTab(
            EXPORT_URL,
            SETTINGS_PAGE_KEYS.EXPORT,
            () => import('features/settings/features/export'),
            'ProjectExportContainer',
          ),
          {
            // Allowlist read from the nav item rather than restated, so the
            // route and the sidebar cannot drift into disagreeing about who may
            // open this page. Not a `guardedTab` because `getAllowlistFor`
            // resolves only against the static SETTINGS_NAV_ITEMS array, and
            // this item is appended on an account scope instead.
            path: EXPORT_GROUPS_URL,
            element: (
              <RoleGuard
                pageKey={SETTINGS_PAGE_KEYS.EXPORT_GROUPS}
                allowlist={EXPORT_GROUPS_NAV_ITEM.roleAllowlist}
              >
                <Outlet />
              </RoleGuard>
            ),
            children: [
              {
                index: true,
                lazy: lazyComponent(
                  () =>
                    import('features/settings/features/export-groups/Container'),
                  'ExportGroupsContainer',
                ),
              },
            ],
          },
          guardedTab(
            INTEGRITY_REPORT_URL,
            SETTINGS_PAGE_KEYS.DATA_INTEGRITY,
            () => import('features/settings/features/data-integrity'),
            'SettingsIntegrityReportContainer',
            [
              {
                path: `${INTEGRITY_FIX_URL}/:checkType`,
                lazy: lazyComponent(
                  () => import('features/settings/features/data-integrity'),
                  'SettingsDuplicateFixContainer',
                ),
              },
            ],
          ),
          guardedTab(
            PROFILE_URL,
            SETTINGS_PAGE_KEYS.PROFILE,
            () => import('features/settings/features/profile'),
            'ProfileContainer',
          ),
        ],
      },
    ],
  },
]
